import ReferenceModel from "../Models/ReferenceModel";
import { BaseService } from "./BaseService";
import axios, { AxiosResponse } from "axios";
import ApiResponse from "../Models/ApiResponse";
import avatarImage from "../Images/person_male.png";
import api from "../Interceptors/Interceptor";
import url from "../constants";
import { CustomerStylingModel } from "./customerStyleService";
import { toast } from "react-toastify";
import UserCommunicationSettingModel from "../Pages/Profile/Settings/CommunicationSetting/Models/UserCommunicationSettingModel";


export interface ICrudUserModel {
    uuid: string
    customer: ReferenceModel
    department: ReferenceModel
    firstName: string
    oldPassword: string
    password: string
    surName: string
    active: boolean
}

export interface IUserModel {
    uuid: string
    customer: ReferenceModel
    department: ReferenceModel
    firstName: string
    surName: string
    active: boolean
}


export interface IUserMeModel {
    uuid: string
    customer: ReferenceModel
    gender: ReferenceModel
    department: ReferenceModel
    firstName: string
    surName?: string
    surname?: string
    username?: string
    email?: string
    phone?: string
    birthdate?: Date | string
    language: string
    timezone: string
    active: boolean
    deleted: boolean
}

export class UserService extends BaseService<IUserModel, ICrudUserModel> {

    protected modelUrl(): string {
        return "security-user";
    }

    public async retrieveMe(): Promise<AxiosResponse<ApiResponse<IUserMeModel>>> {
        return await api.get(`v1/${this.modelUrl()}/me`, {});
    }

    public async retrieveQrCode(): Promise<AxiosResponse<ApiResponse<any>>> {
        return await api.get(`v1/${this.modelUrl()}/qr-code`, {});
    }

    public async saveMultiFactorAuthentication(multiFactorAuthenticationType: ReferenceModel | null): Promise<AxiosResponse<ApiResponse<any>>> {
        return await api.put(`v1/${this.modelUrl()}/multi-factor-authentication-type`, multiFactorAuthenticationType
        );
    }

    public async retrieveMultiFactorAuthentication(): Promise<AxiosResponse<ApiResponse<ReferenceModel>>> {
        return await api.get(`v1/${this.modelUrl()}/multi-factor-authentication-type`);
    }

    public async retrieveUserCommunicationSettings(userIdentifier?: String | null): Promise<AxiosResponse<ApiResponse<UserCommunicationSettingModel[]>>> {

        try {
            const resolvedIdentifier = userIdentifier?.trim();
            if (resolvedIdentifier) {
                return await axios.get(`${url}v1/${this.modelUrl()}/${resolvedIdentifier}/user-communication-value`);
            }

            return await api.get(`v1/${this.modelUrl()}/user-communication-value`);
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async retrieveUserEvents(userIdentifier?: String | null): Promise<AxiosResponse<ApiResponse<any[]>>> {

        try {
            const resolvedIdentifier = userIdentifier?.trim();
            if (resolvedIdentifier) {
                return await axios.get(`${url}v1/${this.modelUrl()}/${resolvedIdentifier}/user-event`);
            }

            return await api.get(`v1/${this.modelUrl()}/user-event`);
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async saveUserEvent(messageEventRecipientModel: any[],userIdentifier?: String | null): Promise<AxiosResponse<ApiResponse<any>>> {

        const resolvedIdentifier = userIdentifier?.trim();
        if (resolvedIdentifier) {
            return await axios.put(`${url}v1/${this.modelUrl()}/${resolvedIdentifier}/user-event`, messageEventRecipientModel);
        }

         return await api.put(`v1/${this.modelUrl()}/user-event`, messageEventRecipientModel);
    }


    public async saveUserCommunicationSettings(userCommunicationSettingModels: UserCommunicationSettingModel[], userIdentifier?: String | null): Promise<AxiosResponse<ApiResponse<any>>> {
        const resolvedIdentifier = userIdentifier?.trim();
        if (resolvedIdentifier) {
            return await axios.put(
                `${url}v1/${this.modelUrl()}/${resolvedIdentifier}/user-communication-value`,
                userCommunicationSettingModels,
            );
        }

        return await api.put(`v1/${this.modelUrl()}/user-communication-value`, userCommunicationSettingModels);
    }

    public async getCustomerStyling(): Promise<AxiosResponse<ApiResponse<CustomerStylingModel[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/security-customer-style`, {});
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async getAvailableRoles(customer?: ReferenceModel): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {

            //TODO (sub)customer specific retrieval of roles
            if (customer !== undefined && customer !== null && customer.uuid !== undefined) {
                let response = await api.get(`v1/${this.modelUrl()}/available-roles?customer=${customer.uuid}`, {});
                return response;
            }

            //Regular retrieval of roles
            let response = await api.get(`v1/${this.modelUrl()}/available-roles`, {});
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async getAssignedPermission(uuid: string): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${uuid}/permissions`, {});
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }
    public async getAvailablePermissions(uuid: string): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${uuid}/available-permissions`, {});
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async saveUserPermission(user: any, permissions: any[]): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.put(`v1/${this.modelUrl()}/${user.uuid}/permission`, permissions);
            return response;
        } catch (error) {
            console.error('Error saving role permissions:', error);
            throw error;
        }
    }


    public async isUserNameUnique(user: string): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${user}/unique-user`);
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async isUserEmailUnique(email: string): Promise<AxiosResponse<ApiResponse<any[]>>> {
        try {
            let response = await api.get(`v1/${this.modelUrl()}/${email}/unique-email`);
            return response;
        } catch (error) {
            console.error('Error fetching parts:', error);
            throw error;
        }
    }

    public async forgotPasswordService(principal: string): Promise<AxiosResponse<ApiResponse<any[]>>> {
        const response = await api.post(`v1/${this.modelUrl()}/reset-password`, { "username": principal });
        return response
    };


    public async fetchProfilePicture(userUuid: string): Promise<string> {
        try {
            const storedAvatars = JSON.parse(localStorage.getItem('userAvatars') || '{}');

            if (storedAvatars[userUuid]) {
                return storedAvatars[userUuid];
            }

            const response = await api.get(`v1/${this.modelUrl()}/${userUuid}/profile-picture`);

            if (!response.data.message || response.data.message === "No profile picture found") {
                return avatarImage;
            }

            //we maken de string schoon just in case
            let base64Image = response.data.message.trim();
            base64Image = base64Image.replace(/^data:image\/png;base64,/, '');
            const fullImage = `data:image/png;base64,${base64Image}`;
            storedAvatars[userUuid] = fullImage;
            localStorage.setItem('userAvatars', JSON.stringify(storedAvatars));

            return fullImage;
        } catch (error) {
            console.error('Error fetching parts:', error);
            return avatarImage;
        }
    }

    public async uploadProfilePicture(base64Image: string): Promise<AxiosResponse<ApiResponse<any>>> {
        try {
            const response = await api.post(`v1/${this.modelUrl()}/profile-picture`, base64Image, {
                headers: { "Content-Type": "text/plain" }
            });
            return response;
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            throw error;
        }
    }

    public async deleteProfilePicture(userUuid: string): Promise<AxiosResponse<ApiResponse<any>>> {
        if (!userUuid) {
            throw new Error("Error deleting profile picture.");
        }

        try {
            const response = await api.delete(`v1/${this.modelUrl()}/profile-picture`);

            localStorage.removeItem(`userAvatar_${userUuid}`);
            const storedAvatars = JSON.parse(localStorage.getItem('userAvatars') || '{}');

            if (storedAvatars[userUuid]) {
                delete storedAvatars[userUuid];
                localStorage.setItem('userAvatars', JSON.stringify(storedAvatars));
            }

            return response;
        } catch (error) {
            console.error('Error deleting profile picture:', error);
            throw error;
        }
    }


    public async otpCheck(): Promise<AxiosResponse<ApiResponse<any>>> {
        const response = await api.post(`v1/${this.modelUrl()}/otp-check`);
        return response
    };

    public checkOtpService = async (auth: any) => {
        const response = await api.post(`v1/${this.modelUrl()}/otp-token-check`, auth);
        return response
    };

    public async getUserForRecipient(customer? : any): Promise<any> {
        if (customer !== undefined && customer !== null && customer.uuid !== undefined) {
            let response = await api.get(`v1/${this.modelUrl()}/recipient-user?customer=${customer.uuid}`, {});
            return response;
        }
        let response = await api.get(`v1/${this.modelUrl()}/recipient-user`, {});
        return response;
    }

}